import { i18n } from "../../globalization/i18n.js";

// ----------------------------------------------------------------------------------

class Validation {


    constructor(validator, message) {

        const alvwaysValid = (value) => true;

        this.validator = validator ?? alvwaysValid;
        this.message = message ?? 'Error!';

    };

    validate(value) {

        return this.validator(value);

    };

    getError(value) {

        if (typeof this.message == 'function')
            return this.message.call(this, value);

        return this.message;

    };

};

// ----------------------------------------------------------------------------------

function createValidationResStr(validator, messageResStr) {

    const validation = new Validation(validator, (value) => i18n.t(messageResStr, { value }));
    return validation;

};


function createValidation(validator, errorMessage) {

    return new Validation(validator, errorMessage);

};

// ----------------------------------------------------------------------------------

const notEmptyValidation = createValidationResStr(

    (value) => {

        if (value === undefined)
            return false;
        if (value === null)
            return false;
        if (value === '')
            return false;

        return true;

    },
    'data.validations.required.error'
);

const intValueValidation = createValidationResStr(
    (value) => {

        if (value === undefined)
            return false;
        if (value === null)
            return false;

        return Number.isInteger(value);

    },
    'data.validations.int_value.error'
);

const floatValueValidation = createValidationResStr(
    (value) => {

        if (value === undefined)
            return false;
        if (value === null)
            return false;

        return Number.isNuner(value);

    },
    'data.validations.float_value.required'
);

const stringValueValidation = createValidationResStr(
    (value) => {

        if (value === undefined)
            return false;
        if (value === null)
            return false;

        if (value === '')
            return true;

        return typeof value == 'string';

    },
    'data.validations.string_value.error'
);

const arrayValueValidation = createValidationResStr(
    (value) => {

        if (value === undefined)
            return false;
        if (value === null)
            return false;

        if (value === '')
            return true;

        return typeof value == 'string';

    },
    'data.validations.array_value.error'
);

const EMAIL_REGEX = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

const emailAddressValidation = createValidationResStr(
    (value) => {

        if (!value)
            return false;

        return EMAIL_REGEX.test(value);

    },
    'data.validations.email_address.error'
);

// ----------------------------------------------------------------------------------

function required() {

    return notEmptyValidation;

};

// ----------------------------------------------------

function intValue() {

    return intValueValidation;

};

function floatValue() {

    return floatValueValidation;

};

function stringValue() {

    return stringValueValidation;

};

function arrayValue() {

    return arrayValueValidation;

};

function codelistValue(codelist, keyProp = 'value') {

    return new Validation(
        (actual) => {
            return codelist.any((codelistItem) => codelistItem[keyProp] == actual);
        },
        (actual) => {
            return i18n.t('data.validations.codelist_value.error', { actual });
        }
    );

};

// ----------------------------------------------------

function equal(expected) {

    return new Validation(
        (actual) => {
            return actual == expected;
        },
        (actual) => {
            return i18n.t('data.validations.equal.error', { actual, expected });
        }
    );

};

function notEqual(expected) {

    return new Validation(
        (actual) => {
            return actual > expected;
        },
        (actual) => {
            return i18n.t('data.validations.not_equal.error', { actual, expected });
        }
    );

};

function greater(expected) {

    return new Validation(
        (actual) => {
            return actual > expected;
        },
        (actual) => {
            return i18n.t('data.validations.greater_than.error', { actual, expected });
        }
    );

};

function greaterOrEqual(expected) {

    return new Validation(
        (actual) => {
            return actual >= expected;
        },
        (actual) => {
            return i18n.t('data.validations.greater_than_or_equal.error', { actual, expected });
        }
    );

};

function less(expected) {

    return new Validation(
        (actual) => {
            return actual < expected;
        },
        (actual) => {
            return i18n.t('data.validations.less_than.error', { actual, expected });
        }
    );

};

function lessOrEqual(expected) {

    return new Validation(
        (actual) => {
            return actual <= expected;
        },
        (actual) => {
            return i18n.t('data.validations.less_than_or_equal.error', { actual, expected });
        }
    );

};

function between(min, max) {

    return new Validation(
        (actual) => {
            return actual >= min && actual <= max;
        },
        (actual) => {
            return i18n.t('data.validations.between.error', { actual, min, max });
        }
    );

};

// ----------------------------------------------------------------------------------

function validateValue(value, validations) {

    if (!(validations && validations.length))
        return true;

    return validations.every(validation => validation.validate(value));

};

function getValueError(value, validations) {

    if (!(validations && validations.length))
        return null;

    let failed = validations.find(validation => !validation.validate(value));

    if (!failed)
        return null;

    return failed.getError();

};

// ----------------------------------------------------------------------------------

export {

    Validation,

    createValidation,
    createValidationResStr,

    // validations

    notEmptyValidation,
    intValueValidation,
    floatValueValidation,
    stringValueValidation,
    arrayValueValidation,
    emailAddressValidation,

    // factory functions

    required,

    intValue,
    floatValue,
    stringValue,
    arrayValue,
    codelistValue,

    equal,
    notEqual,
    greater,
    greaterOrEqual,
    less,
    lessOrEqual,
    between,

    //

    validateValue,
    getValueError

};


